<template>
  <form class="space-y-6 mx-auto px-4 lg:px-0" @submit.prevent="login">
    <div class="input-field">
      <input-label name="eeni">NIB Number</input-label>
      <text-input
        class="bg-platinum-gray rounded-xl"
        name="eeni"
        v-model="form.eeni"
        required
      />
      <input-error :field="$v.eeni" />
    </div>

    <div class="input-field">
      <input-label name="password">Password</input-label>
      <text-input
        class="bg-platinum-gray rounded-xl"
        name="password"
        v-model="form.password"
        required
        type="password"
      />
      <input-error :field="$v.password" />
    </div>

    <div
      class="
        flex flex-col
        lg:flex-row lg:items-center
        items-start
        lg:justify-between lg:space-y-0
        space-y-2
      "
    >
      <div class="text-xs lg:text-sm">
        <a
          class="
            font-medium
            text-indigo-600
            hover:text-indigo-500
            cursor-pointer
          "
          @click="showForgotPassword"
        >
          Forgot your password?
        </a>
      </div>

      <div class="text-xs lg:text-sm">
        <a
          class="
            font-medium
            text-indigo-600
            hover:text-indigo-500
            cursor-pointer
          "
          @click="showResendActivation"
        >
          Resend activation email
        </a>
      </div>
    </div>

    <div>
      <nib-btn
        class="bg-primary hover:bg-primaryLighten w-full rounded-xl"
        dark
        :loading="isLoading"
        type="submit"
        >Sign In</nib-btn
      >
    </div>
    <div class="mt-4 flex justify-between items-center">
      <p class="text-sm lg:text-base">Don't have an account?</p>
      <p>
        <router-link
          :to="{ name: 'register' }"
          class="
            hover:underline
            text-primary text-sm
            lg:text-base
            hover:text-primaryLighten
            font-semibold
          "
        >
          Register here.
        </router-link>
      </p>
    </div>
  </form>
  <div class="bottom fixed bottom-10 right-10">
    <NibSpinner v-if="isLoading" />
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import {
  required,
  numeric,
  minLength,
  maxLength,
  helpers,
} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import useAuth from "/@/composables/useAuth";
import Swal from "/@/plugins/Swal";
import useUserData from "/@/composables/useUserData";
const { authenticateUser } = useAuth();
const { forgotPassword, resendActivationEmail } = useUserData();
const isLoading = ref(false);

const form = reactive({
  eeni: "",
  password: "",
});

const rules = {
  eeni: {
    required,
    numeric,
    minLength: helpers.withMessage(
      () => `N.I numbers are eight digits in length.`,
      minLength(8)
    ),
    maxLength: helpers.withMessage(
      () => `N.I numbers are eight digits in length.`,
      maxLength(8)
    ),
  },
  password: {
    required,
  },
};

const $v: any = useVuelidate(rules, form);

const login = async () => {
  if (!isLoading.value) {
    $v.value.$touch();

    if ($v.value.$invalid) return;

    isLoading.value = true;

    try {
      await authenticateUser(form);
      isLoading.value = false;
    } catch (err) {
      isLoading.value = false;
    }
  }
};

const showForgotPassword = async () => {
  const { value: eeni } = await Swal.fire({
    title: "Forgot Password",
    input: "text",
    inputLabel: "N.I Number",
    inputValue: "",
    showCancelButton: true,
    inputValidator: (value) => {
      if (!value) {
        return "N.I Number is required.";
      }

      return "";
    },
  });

  if (eeni) {
    forgotPassword({ eeni });
  }
};

const showResendActivation = async () => {
  const { value: eeni } = await Swal.fire({
    title: "Resend Activation Email",
    input: "text",
    inputLabel: "N.I Number",
    inputValue: "",
    showCancelButton: true,
    inputValidator: (value) => {
      if (!value) {
        return "N.I Number is required.";
      }

      return "";
    },
  });

  if (eeni) {
    resendActivationEmail({ eeni });
  }
};
</script>

<style lang="postcss" scoped>
form {
  max-width: 400px;
}
</style>
